<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Pen, X } from "lucide-vue-next";

const emit = defineEmits(["change"]);
defineExpose({ getDataURL, clear });

const canvasRef = ref(null);
const hasInk = ref(false);
let drawing = false;
let last = null;
let resizeListener = null;

function setupContext() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = getComputedStyle(canvas).color || "#0f172a";
  ctx.lineWidth = 2.2;
}

function getPos(event) {
  const rect = canvasRef.value.getBoundingClientRect();
  const t = event.touches ? event.touches[0] : event;
  return { x: t.clientX - rect.left, y: t.clientY - rect.top };
}

function start(event) {
  event.preventDefault();
  drawing = true;
  last = getPos(event);
}

function move(event) {
  if (!drawing) return;
  event.preventDefault();
  const ctx = canvasRef.value.getContext("2d");
  const p = getPos(event);
  ctx.beginPath();
  ctx.moveTo(last.x, last.y);
  ctx.lineTo(p.x, p.y);
  ctx.stroke();
  last = p;
  if (!hasInk.value) {
    hasInk.value = true;
    emit("change", true);
  }
}

function end() {
  drawing = false;
}

function clear() {
  const c = canvasRef.value;
  if (!c) return;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  hasInk.value = false;
  emit("change", false);
}

function getDataURL() {
  if (!hasInk.value || !canvasRef.value) return null;
  return canvasRef.value.toDataURL("image/png");
}

onMounted(() => {
  setupContext();
  resizeListener = () => setupContext();
  window.addEventListener("resize", resizeListener);
});

onBeforeUnmount(() => {
  if (resizeListener) window.removeEventListener("resize", resizeListener);
});
</script>

<template>
  <div class="pad">
    <canvas
      ref="canvasRef"
      class="pad-canvas"
      @mousedown="start"
      @mousemove="move"
      @mouseup="end"
      @mouseleave="end"
      @touchstart="start"
      @touchmove="move"
      @touchend="end"
    />
    <div v-if="!hasInk" class="pad-placeholder">
      <Pen class="w-4 h-4" />
      <span>{{ $t("Распишитесь здесь") }}</span>
    </div>
    <div class="pad-baseline" />
    <button type="button" class="pad-reset" :disabled="!hasInk" @click="clear">
      <X class="w-3 h-3" />
      {{ $t("Сбросить") }}
    </button>
  </div>
</template>

<style scoped>
.pad {
  position: relative;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--card));
  height: 220px;
  overflow: hidden;
  color: hsl(var(--foreground));
}

.pad-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
}

.pad-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: hsl(var(--muted-foreground));
  font-size: 13px;
  pointer-events: none;
  user-select: none;
}

.pad-baseline {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 36px;
  height: 1px;
  border-bottom: 1px dashed hsl(var(--border));
  pointer-events: none;
}

.pad-reset {
  position: absolute;
  right: 12px;
  top: 12px;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius);
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.pad-reset:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pad-reset:not(:disabled):hover {
  background: hsl(var(--muted));
}
</style>
